const DEFAULT_FADE_SEC = 0.01;
export function createWebAudioOscillatorFactory(ctx) {
    let buffer = null;
    let rootKey = 60;
    let loopEnabled = false;
    let loopStartSec = 0;
    let loopEndSec = 0;
    const activeEntries = new Set();
    function toAudioBuffer(samples, sampleRate) {
        const length = samples.length;
        const audioBuffer = ctx.createBuffer(1, length, sampleRate);
        const channelData = audioBuffer.getChannelData(0);
        if (samples instanceof Float32Array) {
            channelData.set(samples);
        }
        else {
            for (let i = 0; i < length; i++) {
                channelData[i] = samples[i] / 32768;
            }
        }
        return audioBuffer;
    }
    function removeEntry(entry) {
        if (!activeEntries.has(entry))
            return;
        activeEntries.delete(entry);
        try {
            entry.source.disconnect();
        }
        catch { /* already disconnected */ }
        try {
            entry.gain.disconnect();
        }
        catch { /* already disconnected */ }
    }
    function stopAllActive() {
        const entries = [...activeEntries];
        for (const entry of entries) {
            entry.osc.stop(0);
        }
    }
    return {
        setBuffer(samples, sampleRate) {
            if (activeEntries.size > 0) {
                stopAllActive();
            }
            buffer = toAudioBuffer(samples, sampleRate);
        },
        setRootKey(key) {
            rootKey = key;
        },
        setLoopRegion(enabled, loopStart, loopEnd) {
            loopEnabled = enabled;
            loopStartSec = loopStart;
            loopEndSec = loopEnd;
            for (const entry of activeEntries) {
                entry.source.loop = enabled;
                if (enabled) {
                    entry.source.loopStart = loopStart;
                    entry.source.loopEnd = loopEnd;
                }
                entry.osc.setLoopRegion(enabled, loopStart, loopEnd);
            }
        },
        createOscillator(note, velocity) {
            if (!buffer) {
                throw new Error('No sample buffer loaded — call setBuffer() before createOscillator()');
            }
            const source = ctx.createBufferSource();
            const gainNode = ctx.createGain();
            source.buffer = buffer;
            source.playbackRate.value = Math.pow(2, (note - rootKey) / 12);
            gainNode.gain.value = velocity / 127;
            source.loop = loopEnabled;
            if (loopEnabled) {
                source.loopStart = loopStartSec;
                source.loopEnd = loopEndSec;
            }
            source.connect(gainNode);
            gainNode.connect(ctx.destination);
            let playing = true;
            let entry;
            const osc = {
                get note() { return note; },
                get isPlaying() { return playing; },
                setLoopRegion(enabled, startSec, endSec) {
                    source.loop = enabled;
                    if (enabled) {
                        source.loopStart = startSec;
                        source.loopEnd = endSec;
                    }
                },
                stop(fadeTimeSec) {
                    if (!playing)
                        return;
                    playing = false;
                    const fade = fadeTimeSec ?? DEFAULT_FADE_SEC;
                    const now = ctx.currentTime;
                    gainNode.gain.setValueAtTime(gainNode.gain.value, now);
                    gainNode.gain.linearRampToValueAtTime(0, now + fade);
                    try {
                        source.stop(now + fade);
                    }
                    catch {
                        /* already stopped */
                    }
                    removeEntry(entry);
                },
            };
            entry = { osc, source, gain: gainNode };
            activeEntries.add(entry);
            source.onended = () => {
                playing = false;
                removeEntry(entry);
            };
            source.start();
            return osc;
        },
        createSliceOscillator(startSample, endSample, velocity) {
            if (!buffer) {
                throw new Error('No sample buffer loaded — call setBuffer() before createSliceOscillator()');
            }
            const source = ctx.createBufferSource();
            const gainNode = ctx.createGain();
            source.buffer = buffer;
            source.playbackRate.value = 1.0;
            gainNode.gain.value = velocity / 127;
            source.loop = false;
            source.connect(gainNode);
            gainNode.connect(ctx.destination);
            let playing = true;
            let entry;
            const osc = {
                get note() { return -1; },
                get isPlaying() { return playing; },
                setLoopRegion(_enabled, _startSec, _endSec) {
                    // Slices are one-shot; loop region is not applicable.
                },
                stop(fadeTimeSec) {
                    if (!playing)
                        return;
                    playing = false;
                    const fade = fadeTimeSec ?? DEFAULT_FADE_SEC;
                    const now = ctx.currentTime;
                    gainNode.gain.setValueAtTime(gainNode.gain.value, now);
                    gainNode.gain.linearRampToValueAtTime(0, now + fade);
                    try {
                        source.stop(now + fade);
                    }
                    catch {
                        /* already stopped */
                    }
                    removeEntry(entry);
                },
            };
            entry = { osc, source, gain: gainNode };
            activeEntries.add(entry);
            source.onended = () => {
                playing = false;
                removeEntry(entry);
            };
            const offsetSec = startSample / buffer.sampleRate;
            const durationSec = (endSample - startSample) / buffer.sampleRate;
            source.start(0, offsetSec, durationSec);
            return osc;
        },
        dispose() {
            stopAllActive();
            buffer = null;
        },
    };
}
//# sourceMappingURL=web-audio-oscillator-factory.js.map